import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Box, CircularProgress } from "@mui/material";
import { pgAPI } from "../api/api";
import { getImageUrl } from "../config";
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons - moved outside component to prevent re-renders
const userLocationIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const selectedLocationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map components moved OUTSIDE to prevent re-renders
function UserLocationMarker({ userLocation }) {
  const map = useMap();
  
  useEffect(() => {
    if (userLocation && map) {
      map.setView([userLocation.lat, userLocation.lng], 16);
    }
  }, [userLocation, map]);

  if (!userLocation) return null;
  
  return (
    <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
      <Popup>📍 Your Current Location</Popup>
    </Marker>
  );
}

function LocationMarker({ onLocationSelect, selectedLocation }) {
  const map = useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });

  const lat = parseFloat(selectedLocation?.lat);
  const lng = parseFloat(selectedLocation?.lng);
  
  if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return null;
  
  return (
    <Marker position={[lat, lng]} icon={selectedLocationIcon}>
      <Popup>📍 Property Location</Popup>
    </Marker>
  );
}

// Initial state for location
const initialLocation = {
  address: "",
  area: "",
  road: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
  country: "",
  lat: null,
  lng: null
};

// Initial state for room rates
const initialRoomRates = {
  single_sharing: "",
  double_sharing: "",
  triple_sharing: "",
  four_sharing: "",
  single_room: "",
  double_room: "",
  price_1bhk: "",
  price_2bhk: "",
  price_3bhk: "",
  price_4bhk: "",
  co_living_single_room: "",
  co_living_double_room: "",
  coliving_three_sharing: "",
  coliving_four_sharing: "",
};

// Initial state for BHK config
const initialBhkConfig = {
  bedrooms_1bhk: "",
  bathrooms_1bhk: "",
  bedrooms_2bhk: "",
  bathrooms_2bhk: "",
  bedrooms_3bhk: "",
  bathrooms_3bhk: "",
  bedrooms_4bhk: "",
  bathrooms_4bhk: "",
};

function EditPG() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  
  const [pageLoading, setPageLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [manualEditMode, setManualEditMode] = useState(false);
  
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [roomRates, setRoomRates] = useState(initialRoomRates);
  const [bhkConfig, setBhkConfig] = useState(initialBhkConfig);
  
  const [form, setForm] = useState({
    pg_name: "",
    pg_type: "boys",
    pg_category: "pg",
    bhk_type: "1",
    furnishing_type: "unfurnished",
    
    food_available: false,
    food_type: "veg",
    meals_per_day: "",
    ac_available: false,
    wifi_available: false,
    tv: false,
    parking_available: false,
    bike_parking: false,
    laundry_available: false,
    washing_machine: false,
    refrigerator: false,
    microwave: false,
    geyser: false,
    power_backup: false,
    lift_elevator: false,
    cctv: false,
    security_guard: false,
    gym: false,
    housekeeping: false,
    water_purifier: false,
    fire_safety: false,
    study_room: false,
    common_tv_lounge: false,
    balcony_open_space: false,
    water_24x7: false,
    water_type: "borewell",
    
    cupboard_available: false,
    table_chair_available: false,
    dining_table_available: false,
    attached_bathroom: false,
    balcony_available: false,
    wall_mounted_clothes_hook: false,
    bed_with_mattress: false,
    fan_light: false,
    kitchen_room: false,
    
    co_living_fully_furnished: false,
    co_living_food_included: false,
    co_living_wifi_included: false,
    co_living_housekeeping: false,
    co_living_power_backup: false,
    co_living_maintenance: false,
    
    visitor_allowed: false,
    visitor_time_restricted: false,
    visitors_allowed_till: "",
    couple_allowed: false,
    family_allowed: false,
    smoking_allowed: false,
    drinking_allowed: false,
    pets_allowed: false,
    late_night_entry_allowed: false,
    entry_curfew_time: "",
    outside_food_allowed: false,
    parties_allowed: false,
    loud_music_restricted: false,
    lock_in_period: false,
    min_stay_months: "0",
    min_stay_available: false,
    min_stay_days: "",
    notice_period: "1",
    agreement_mandatory: true,
    id_proof_mandatory: false,
    office_going_only: false,
    students_only: false,
    boys_only: false,
    girls_only: false,
    co_living_allowed: false,
    subletting_allowed: false,
    
    security_deposit: "",
    maintenance_amount: "",
    
    total_rooms: "",
    available_rooms: "",
    
    description: "",
    
    contact_person: "",
    contact_email: "",
    contact_phone: "",
  });

  const mapRef = useRef(null);
  const isToLet = form.pg_category === "to_let";
  const isPG = form.pg_category === "pg";
  const isCoLiving = form.pg_category === "coliving";

  // Add CSS animation properly
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  /* ================= AUTH + LOAD ================= */
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Load property data
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!id || !user) return;
      
      try {
        setPageLoading(true);
        console.log("Fetching property with ID:", id);
        
        const response = await pgAPI.getProperty(id);
        
        if (response.data && response.data.success) {
          const data = response.data.data;
          console.log("Property data loaded:", data);
          
          // Update form state with proper boolean conversion
          setForm(prev => ({
            ...prev,
            ...data,
            security_deposit: data.security_deposit || data.deposit_amount || "",
            food_available: data.food_available === true || data.food_available === 'true',
            ac_available: data.ac_available === true || data.ac_available === 'true',
            wifi_available: data.wifi_available === true || data.wifi_available === 'true',
            tv: data.tv === true || data.tv === 'true',
            parking_available: data.parking_available === true || data.parking_available === 'true',
            bike_parking: data.bike_parking === true || data.bike_parking === 'true',
            laundry_available: data.laundry_available === true || data.laundry_available === 'true',
            washing_machine: data.washing_machine === true || data.washing_machine === 'true',
            refrigerator: data.refrigerator === true || data.refrigerator === 'true',
            microwave: data.microwave === true || data.microwave === 'true',
            geyser: data.geyser === true || data.geyser === 'true',
            power_backup: data.power_backup === true || data.power_backup === 'true',
            lift_elevator: data.lift_elevator === true || data.lift_elevator === 'true',
            cctv: data.cctv === true || data.cctv === 'true',
            security_guard: data.security_guard === true || data.security_guard === 'true',
            gym: data.gym === true || data.gym === 'true',
            housekeeping: data.housekeeping === true || data.housekeeping === 'true',
            water_purifier: data.water_purifier === true || data.water_purifier === 'true',
            fire_safety: data.fire_safety === true || data.fire_safety === 'true',
            study_room: data.study_room === true || data.study_room === 'true',
            common_tv_lounge: data.common_tv_lounge === true || data.common_tv_lounge === 'true',
            balcony_open_space: data.balcony_open_space === true || data.balcony_open_space === 'true',
            water_24x7: data.water_24x7 === true || data.water_24x7 === 'true',
            water_type: data.water_type || "borewell",
            
            cupboard_available: data.cupboard_available === true || data.cupboard_available === 'true',
            table_chair_available: data.table_chair_available === true || data.table_chair_available === 'true',
            dining_table_available: data.dining_table_available === true || data.dining_table_available === 'true',
            attached_bathroom: data.attached_bathroom === true || data.attached_bathroom === 'true',
            balcony_available: data.balcony_available === true || data.balcony_available === 'true',
            wall_mounted_clothes_hook: data.wall_mounted_clothes_hook === true || data.wall_mounted_clothes_hook === 'true',
            bed_with_mattress: data.bed_with_mattress === true || data.bed_with_mattress === 'true',
            fan_light: data.fan_light === true || data.fan_light === 'true',
            kitchen_room: data.kitchen_room === true || data.kitchen_room === 'true',
            
            co_living_fully_furnished: data.co_living_fully_furnished === true || data.co_living_fully_furnished === 'true',
            co_living_food_included: data.co_living_food_included === true || data.co_living_food_included === 'true',
            co_living_wifi_included: data.co_living_wifi_included === true || data.co_living_wifi_included === 'true',
            co_living_housekeeping: data.co_living_housekeeping === true || data.co_living_housekeeping === 'true',
            co_living_power_backup: data.co_living_power_backup === true || data.co_living_power_backup === 'true',
            co_living_maintenance: data.co_living_maintenance === true || data.co_living_maintenance === 'true',
            
            visitor_allowed: data.visitor_allowed === true || data.visitor_allowed === 'true',
            visitor_time_restricted: data.visitor_time_restricted === true || data.visitor_time_restricted === 'true',
            couple_allowed: data.couple_allowed === true || data.couple_allowed === 'true',
            family_allowed: data.family_allowed === true || data.family_allowed === 'true',
            smoking_allowed: data.smoking_allowed === true || data.smoking_allowed === 'true',
            drinking_allowed: data.drinking_allowed === true || data.drinking_allowed === 'true',
            pets_allowed: data.pets_allowed === true || data.pets_allowed === 'true',
            late_night_entry_allowed: data.late_night_entry_allowed === true || data.late_night_entry_allowed === 'true',
            outside_food_allowed: data.outside_food_allowed === true || data.outside_food_allowed === 'true',
            parties_allowed: data.parties_allowed === true || data.parties_allowed === 'true',
            loud_music_restricted: data.loud_music_restricted === true || data.loud_music_restricted === 'true',
            lock_in_period: data.lock_in_period === true || data.lock_in_period === 'true',
            agreement_mandatory: data.agreement_mandatory === true || data.agreement_mandatory === 'true',
            id_proof_mandatory: data.id_proof_mandatory === true || data.id_proof_mandatory === 'true',
            office_going_only: data.office_going_only === true || data.office_going_only === 'true',
            students_only: data.students_only === true || data.students_only === 'true',
            boys_only: data.boys_only === true || data.boys_only === 'true',
            girls_only: data.girls_only === true || data.girls_only === 'true',
            co_living_allowed: data.co_living_allowed === true || data.co_living_allowed === 'true',
            subletting_allowed: data.subletting_allowed === true || data.subletting_allowed === 'true',
            min_stay_available: data.min_stay_available === true || data.min_stay_available === 'true',
            min_stay_days: data.min_stay_days || "",
          }));
          
          // Update location state
          setSelectedLocation({
            address: data.address || "",
            area: data.area || "",
            road: data.road || "",
            landmark: data.landmark || "",
            city: data.city || "",
            state: data.state || "",
            pincode: data.pincode || "",
            country: data.country || "",
            lat: data.latitude || null,
            lng: data.longitude || null
          });
          
          // Update room rates
          setRoomRates(prev => ({
            ...prev,
            single_sharing: data.single_sharing || "",
            double_sharing: data.double_sharing || "",
            triple_sharing: data.triple_sharing || "",
            four_sharing: data.four_sharing || "",
            single_room: data.single_room || "",
            double_room: data.double_room || "",
            price_1bhk: data.price_1bhk || "",
            price_2bhk: data.price_2bhk || "",
            price_3bhk: data.price_3bhk || "",
            price_4bhk: data.price_4bhk || "",
            co_living_single_room: data.co_living_single_room || "",
            co_living_double_room: data.co_living_double_room || "",
            coliving_three_sharing: data.coliving_three_sharing || "",
            coliving_four_sharing: data.coliving_four_sharing || "",
          }));
          
          // Update BHK config
          setBhkConfig(prev => ({
            ...prev,
            bedrooms_1bhk: data.bedrooms_1bhk || "",
            bathrooms_1bhk: data.bathrooms_1bhk || "",
            bedrooms_2bhk: data.bedrooms_2bhk || "",
            bathrooms_2bhk: data.bathrooms_2bhk || "",
            bedrooms_3bhk: data.bedrooms_3bhk || "",
            bathrooms_3bhk: data.bathrooms_3bhk || "",
            bedrooms_4bhk: data.bedrooms_4bhk || "",
            bathrooms_4bhk: data.bathrooms_4bhk || "",
          }));
          
          // Set existing photos
          if (data.photos && Array.isArray(data.photos)) {
            setExistingPhotos(data.photos);
          }
        } else {
          console.error("Failed to load property:", response.data?.message);
          alert("Failed to load property data");
          navigate("/owner/dashboard");
        }
      } catch (error) {
        console.error("Error fetching property:", error);
        alert("Failed to load property data. Please try again.");
        navigate("/owner/dashboard");
      } finally {
        setPageLoading(false);
      }
    };
    
    if (id && user && role === "owner") {
      fetchPropertyData();
    }
  }, [id, user, role, navigate]);

  // Update co-living inclusions when category changes
  useEffect(() => {
    if (isCoLiving) {
      setForm(prev => ({
        ...prev,
        co_living_fully_furnished: true,
        co_living_food_included: true,
        co_living_wifi_included: true,
        co_living_housekeeping: true,
        co_living_power_backup: true,
        co_living_maintenance: true,
      }));
    }
  }, [form.pg_category]);

  // Update pg_type based on gender selection
  useEffect(() => {
    if (form.boys_only) {
      setForm(prev => ({ ...prev, pg_type: "boys" }));
    } else if (form.girls_only) {
      setForm(prev => ({ ...prev, pg_type: "girls" }));
    } else if (form.co_living_allowed) {
      setForm(prev => ({ ...prev, pg_type: "coliving" }));
    }
  }, [form.boys_only, form.girls_only, form.co_living_allowed]);

  // Format coordinate for display
  const formatCoordinate = (coord) => {
    if (!coord && coord !== 0) return "";
    const num = parseFloat(coord);
    return isNaN(num) ? "" : num.toFixed(6);
  };

  // Parse coordinate from input
  const parseCoordinate = (value) => {
    if (!value && value !== 0) return "";
    const num = parseFloat(value);
    return isNaN(num) ? "" : num;
  };

  // Get user's current location
  const getUserCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        await fetchAddressFromCoordinates(latitude, longitude);
        
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 16);
        }
        
        setGettingLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        setGettingLocation(false);
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert("Location access denied. Please enable location services in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            alert("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            alert("Location request timed out.");
            break;
          default:
            alert("An unknown error occurred while getting your location.");
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Fetch address from coordinates using OpenStreetMap Nominatim
  const fetchAddressFromCoordinates = async (lat, lng) => {
    try {
      setMapLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
      );
      
      const data = await response.json();
      
      if (data) {
        const address = data.display_name || "";
        const addressParts = data.address || {};
        
        setSelectedLocation(prev => ({
          ...prev,
          address: address,
          area: addressParts.neighbourhood || addressParts.suburb || addressParts.village || "",
          road: addressParts.road || "",
          landmark: addressParts.amenity || "",
          city: addressParts.city || addressParts.town || addressParts.county || "",
          state: addressParts.state || "",
          pincode: addressParts.postcode || "",
          country: addressParts.country || "",
          lat: lat,
          lng: lng
        }));
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setSelectedLocation(prev => ({
        ...prev,
        lat: lat,
        lng: lng
      }));
    } finally {
      setMapLoading(false);
    }
  };

  // Handle location selection from map
  const handleLocationSelect = async (lat, lng) => {
    await fetchAddressFromCoordinates(lat, lng);
  };

  // Search location by address
  const searchLocation = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    try {
      setMapLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&accept-language=en`
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = data[0];
        const lat = parseFloat(location.lat);
        const lng = parseFloat(location.lon);
        
        setSelectedLocation(prev => ({
          ...prev,
          lat: lat,
          lng: lng
        }));
        
        await fetchAddressFromCoordinates(lat, lng);
        
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 16);
        }
      }
    } catch (error) {
      console.error("Error searching location:", error);
      alert("Location not found. Please try a different search.");
    } finally {
      setMapLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle exclusive gender selection
    if (name === "boys_only" && checked) {
      setForm(prev => ({ 
        ...prev, 
        [name]: checked,
        girls_only: false,
        co_living_allowed: false
      }));
    } else if (name === "girls_only" && checked) {
      setForm(prev => ({ 
        ...prev, 
        [name]: checked,
        boys_only: false,
        co_living_allowed: false
      }));
    } else if (name === "co_living_allowed" && checked) {
      setForm(prev => ({ 
        ...prev, 
        [name]: checked,
        boys_only: false,
        girls_only: false
      }));
    } else {
      setForm({
        ...form,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;
    setRoomRates({
      ...roomRates,
      [name]: value
    });
  };

  const handleBhkConfigChange = (e) => {
    const { name, value } = e.target;
    setBhkConfig({
      ...bhkConfig,
      [name]: value
    });
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "lat" || name === "lng") {
      const numValue = value === "" ? "" : parseFloat(value);
      setSelectedLocation(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? "" : numValue
      }));
    } else {
      setSelectedLocation(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length + existingPhotos.length - photosToDelete.length > 10) {
      alert("Maximum 10 photos allowed");
      return;
    }
    setPhotos([...photos, ...files]);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (index, photoUrl) => {
    setExistingPhotos(existingPhotos.filter((_, i) => i !== index));
    setPhotosToDelete([...photosToDelete, photoUrl]);
  };

  // Validation function
  const validateForm = () => {
    if (!form.pg_name?.trim()) return "Enter property name";
    if (!selectedLocation.address?.trim()) return "Select location from map or enter manually";
    if (!form.contact_person?.trim()) return "Enter contact person name";
    if (!form.contact_phone?.trim()) return "Enter contact phone number";
    
    // Email validation (optional but must be valid if provided)
    if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
      return "Enter a valid email address";
    }
    
    // Phone validation
    if (!/^[0-9]{10,15}$/.test(form.contact_phone.replace(/\D/g, ''))) {
      return "Enter a valid phone number (10-15 digits)";
    }
    
    return null;
  };

  // Helper to append only non-empty values
  const appendIfValue = (formData, key, value) => {
    if (value !== "" && value !== null && value !== undefined) {
      formData.append(key, value.toString());
    }
  };

  // Handle form submission using pgAPI convenience method
  const handleSubmit = async () => {
    if (!user) {
      alert("Please log in to update a property");
      navigate("/login");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setUpdating(true);

      const formData = new FormData();
      
      // Basic info
      formData.append("pg_name", form.pg_name);
      formData.append("pg_category", form.pg_category);
      formData.append("pg_type", form.pg_type);
      
      // Location details
      formData.append("address", selectedLocation.address);
      formData.append("area", selectedLocation.area);
      appendIfValue(formData, "road", selectedLocation.road);
      appendIfValue(formData, "landmark", selectedLocation.landmark);
      formData.append("city", selectedLocation.city);
      appendIfValue(formData, "state", selectedLocation.state);
      appendIfValue(formData, "pincode", selectedLocation.pincode);
      appendIfValue(formData, "country", selectedLocation.country);
      
      // Coordinates
      const lat = parseCoordinate(selectedLocation.lat);
      const lng = parseCoordinate(selectedLocation.lng);
      if (lat) formData.append("latitude", lat.toString());
      if (lng) formData.append("longitude", lng.toString());
      
      // BHK Configuration for To-Let
      if (isToLet) {
        formData.append("bhk_type", form.bhk_type);
        formData.append("furnishing_type", form.furnishing_type);
        
        // BHK Prices
        appendIfValue(formData, "price_1bhk", roomRates.price_1bhk);
        appendIfValue(formData, "price_2bhk", roomRates.price_2bhk);
        appendIfValue(formData, "price_3bhk", roomRates.price_3bhk);
        appendIfValue(formData, "price_4bhk", roomRates.price_4bhk);
        
        // BHK Room/Bath counts
        appendIfValue(formData, "bedrooms_1bhk", bhkConfig.bedrooms_1bhk);
        appendIfValue(formData, "bathrooms_1bhk", bhkConfig.bathrooms_1bhk);
        appendIfValue(formData, "bedrooms_2bhk", bhkConfig.bedrooms_2bhk);
        appendIfValue(formData, "bathrooms_2bhk", bhkConfig.bathrooms_2bhk);
        appendIfValue(formData, "bedrooms_3bhk", bhkConfig.bedrooms_3bhk);
        appendIfValue(formData, "bathrooms_3bhk", bhkConfig.bathrooms_3bhk);
        appendIfValue(formData, "bedrooms_4bhk", bhkConfig.bedrooms_4bhk);
        appendIfValue(formData, "bathrooms_4bhk", bhkConfig.bathrooms_4bhk);
      }
      
      // Standard PG Room Rates
      if (isPG) {
        Object.keys(roomRates).forEach(key => {
          if (key.startsWith("single_") || key.startsWith("double_") || key.startsWith("triple_") || key.startsWith("four_")) {
            appendIfValue(formData, key, roomRates[key]);
          }
        });
      }
      
      // Co-Living Rates (including new sharing types)
      if (isCoLiving) {
        appendIfValue(formData, "co_living_single_room", roomRates.co_living_single_room);
        appendIfValue(formData, "co_living_double_room", roomRates.co_living_double_room);
        appendIfValue(formData, "coliving_three_sharing", roomRates.coliving_three_sharing);
        appendIfValue(formData, "coliving_four_sharing", roomRates.coliving_four_sharing);
      }
      
      // FACILITIES
      const facilities = [
        "food_available", "ac_available", "wifi_available", "tv",
        "parking_available", "bike_parking", "laundry_available",
        "washing_machine", "refrigerator", "microwave", "geyser",
        "power_backup", "lift_elevator", "cctv", "security_guard",
        "gym", "housekeeping", "water_purifier", "fire_safety",
        "study_room", "common_tv_lounge", "balcony_open_space",
        "water_24x7", "cupboard_available", "table_chair_available",
        "dining_table_available", "attached_bathroom", "balcony_available",
        "wall_mounted_clothes_hook", "bed_with_mattress", "fan_light", "kitchen_room"
      ];
      
      facilities.forEach(key => {
        formData.append(key, form[key] ? "true" : "false");
      });
      
      appendIfValue(formData, "food_type", form.food_type);
      appendIfValue(formData, "meals_per_day", form.meals_per_day);
      appendIfValue(formData, "water_type", form.water_type);
      
      // Co-Living Inclusions
      if (isCoLiving) {
        formData.append("co_living_fully_furnished", "true");
        formData.append("co_living_food_included", "true");
        formData.append("co_living_wifi_included", "true");
        formData.append("co_living_housekeeping", "true");
        formData.append("co_living_power_backup", "true");
        formData.append("co_living_maintenance", "true");
      } else {
        formData.append("co_living_fully_furnished", form.co_living_fully_furnished ? "true" : "false");
        formData.append("co_living_food_included", form.co_living_food_included ? "true" : "false");
        formData.append("co_living_wifi_included", form.co_living_wifi_included ? "true" : "false");
        formData.append("co_living_housekeeping", form.co_living_housekeeping ? "true" : "false");
        formData.append("co_living_power_backup", form.co_living_power_backup ? "true" : "false");
        formData.append("co_living_maintenance", form.co_living_maintenance ? "true" : "false");
      }
      
      // RULES
      const rules = [
        "visitor_allowed", "visitor_time_restricted", "couple_allowed",
        "family_allowed", "smoking_allowed", "drinking_allowed",
        "pets_allowed", "late_night_entry_allowed", "outside_food_allowed",
        "parties_allowed", "loud_music_restricted", "lock_in_period",
        "agreement_mandatory", "id_proof_mandatory", "office_going_only",
        "students_only", "subletting_allowed"
      ];
      
      rules.forEach(key => {
        formData.append(key, form[key] ? "true" : "false");
      });
      
      // Gender rules
      formData.append("boys_only", form.boys_only ? "true" : "false");
      formData.append("girls_only", form.girls_only ? "true" : "false");
      formData.append("co_living_allowed", form.co_living_allowed ? "true" : "false");
      
      // Minimum stay fields
      formData.append("min_stay_available", form.min_stay_available ? "true" : "false");
      appendIfValue(formData, "min_stay_days", form.min_stay_days);
      
      appendIfValue(formData, "visitors_allowed_till", form.visitors_allowed_till);
      appendIfValue(formData, "entry_curfew_time", form.entry_curfew_time);
      appendIfValue(formData, "min_stay_months", form.min_stay_months);
      formData.append("notice_period", form.notice_period);
      
      // Additional charges
      appendIfValue(formData, "security_deposit", form.security_deposit);
      appendIfValue(formData, "maintenance_amount", form.maintenance_amount);
      
      // Room details
      appendIfValue(formData, "total_rooms", form.total_rooms);
      appendIfValue(formData, "available_rooms", form.available_rooms);
      
      // Description
      formData.append("description", form.description);
      
      // Contact info
      formData.append("contact_person", form.contact_person);
      appendIfValue(formData, "contact_email", form.contact_email);
      formData.append("contact_phone", form.contact_phone);
      
      // Photos to delete
      photosToDelete.forEach(photoUrl => {
        formData.append("photos_to_delete[]", photoUrl);
      });
      
      // New photos
      photos.forEach((photo) => {
        formData.append("photos", photo);
      });

      console.log("Updating property with ID:", id);
      
      const response = await pgAPI.updateProperty(id, formData);

      if (response.data && response.data.success) {
        alert(`✅ Property Updated Successfully!`);
        navigate("/owner/dashboard");
      } else {
        alert(`❌ Failed: ${response.data?.message || "Unknown error"}`);
      }

    } catch (err) {
      console.error("Error:", err.response?.data || err.message);
      
      let errorMessage = "Unknown error";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      alert(`❌ Failed to update property: ${errorMessage}`);
    } finally {
      setUpdating(false);
    }
  };

  // OpenStreetMap Modal Component
  const OpenStreetMapModal = () => {
    const displayLat = formatCoordinate(selectedLocation.lat);
    const displayLng = formatCoordinate(selectedLocation.lng);
    const mapLat = parseCoordinate(selectedLocation.lat) || 12.9716;
    const mapLng = parseCoordinate(selectedLocation.lng) || 77.5946;
    
    return (
      <div style={styles.mapModalOverlay}>
        <div style={styles.mapModal}>
          <div style={styles.mapHeader}>
            <h3>Edit Property Location</h3>
            <div style={styles.mapControls}>
              <button 
                onClick={getUserCurrentLocation}
                style={styles.currentLocationButton}
                disabled={gettingLocation || mapLoading}
                title="Use my current location"
              >
                {gettingLocation ? "📍 Getting Location..." : "📍 Use My Location"}
              </button>
              <div style={styles.mapSearch}>
                <input
                  type="text"
                  placeholder="Search for area, landmark, or address..."
                  id="location-search"
                  style={styles.searchInput}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      searchLocation(e.target.value);
                    }
                  }}
                />
                <button 
                  onClick={() => {
                    const searchInput = document.getElementById('location-search');
                    if (searchInput && searchInput.value) {
                      searchLocation(searchInput.value);
                    }
                  }}
                  style={styles.searchButton}
                  disabled={mapLoading}
                >
                  {mapLoading ? "Searching..." : "🔍 Search"}
                </button>
              </div>
            </div>
            <button onClick={() => setShowMap(false)} style={styles.closeBtn}>✕</button>
          </div>
          
          <div style={styles.mapContainer}>
            {mapLoading || gettingLocation ? (
              <div style={styles.loadingOverlay}>
                <div style={styles.spinner}></div>
                <p>{gettingLocation ? "Getting your location..." : "Loading map..."}</p>
              </div>
            ) : null}
            
            <MapContainer
              center={[mapLat, mapLng]}
              zoom={13}
              style={styles.leafletMap}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <UserLocationMarker userLocation={userLocation} />
              <LocationMarker 
                onLocationSelect={handleLocationSelect}
                selectedLocation={selectedLocation}
              />
            </MapContainer>
            
            <div style={styles.locationPreview}>
              <h4>Selected Location Details:</h4>
              {mapLoading ? (
                <p>Fetching address details...</p>
              ) : (
                <>
                  <p><strong>Coordinates:</strong> {displayLat}, {displayLng}</p>
                  {selectedLocation.address && (
                    <p><strong>Address:</strong> {selectedLocation.address}</p>
                  )}
                </>
              )}
            </div>
            
            <div style={styles.mapFooter}>
              <button 
                onClick={() => {
                  const lat = parseCoordinate(selectedLocation.lat);
                  const lng = parseCoordinate(selectedLocation.lng);
                  
                  if (!lat || !lng) {
                    alert("Please select a location by clicking on the map");
                    return;
                  }
                  setShowMap(false);
                }}
                style={styles.confirmButton}
                disabled={mapLoading || gettingLocation}
              >
                ✅ Confirm This Location
              </button>
              <button 
                onClick={() => {
                  setShowMap(false);
                  setManualEditMode(true);
                }}
                style={styles.manualButton}
              >
                ✏️ Enter Address Manually
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Manual Address Entry Form
  const ManualAddressForm = () => {
    const [localLocation, setLocalLocation] = useState(selectedLocation);
    const [isSaving, setIsSaving] = useState(false);
    
    useEffect(() => {
      setLocalLocation(selectedLocation);
    }, [selectedLocation]);
    
    const handleLocalChange = (e) => {
      const { name, value } = e.target;
      setLocalLocation(prev => ({
        ...prev,
        [name]: value
      }));
    };
    
    const handleSave = () => {
      if (!localLocation.address || !localLocation.area || !localLocation.city || !localLocation.state || !localLocation.pincode || !localLocation.country) {
        alert("Please fill all required fields (marked with *)");
        return;
      }
      
      setIsSaving(true);
      setSelectedLocation(localLocation);
      setIsSaving(false);
      setManualEditMode(false);
    };
    
    const handleBackToMap = () => {
      setManualEditMode(false);
      setShowMap(true);
    };
    
    return (
      <div style={styles.manualModalOverlay}>
        <div style={styles.manualModal}>
          <div style={styles.manualHeader}>
            <h3>Edit Address Manually</h3>
            <button onClick={() => setManualEditMode(false)} style={styles.closeBtn}>✕</button>
          </div>
          
          <div style={styles.manualForm}>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label>Full Address *</label>
                <textarea
                  name="address"
                  value={localLocation.address}
                  onChange={handleLocalChange}
                  placeholder="Complete address including floor, building, street"
                  style={styles.textarea}
                  rows="3"
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Area/Locality *</label>
                <input
                  type="text"
                  name="area"
                  value={localLocation.area}
                  onChange={handleLocalChange}
                  placeholder="e.g., Koramangala, Whitefield"
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Road/Street</label>
                <input
                  type="text"
                  name="road"
                  value={localLocation.road}
                  onChange={handleLocalChange}
                  placeholder="e.g., MG Road, 100 Feet Road"
                  style={styles.input}
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  value={localLocation.landmark}
                  onChange={handleLocalChange}
                  placeholder="e.g., Near Forum Mall, Opposite Metro Station"
                  style={styles.input}
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={localLocation.city}
                  onChange={handleLocalChange}
                  placeholder="e.g., Bangalore"
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={localLocation.state}
                  onChange={handleLocalChange}
                  placeholder="e.g., Karnataka"
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Pincode *</label>
                <input
                  type="text"
                  name="pincode"
                  value={localLocation.pincode}
                  onChange={handleLocalChange}
                  placeholder="e.g., 560034"
                  style={styles.input}
                  required
                  pattern="[0-9]{6}"
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Country *</label>
                <input
                  type="text"
                  name="country"
                  value={localLocation.country}
                  onChange={handleLocalChange}
                  placeholder="e.g., India"
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Latitude (optional)</label>
                <input
                  type="text"
                  name="lat"
                  value={localLocation.lat === null ? "" : localLocation.lat}
                  onChange={handleLocalChange}
                  placeholder="e.g., 12.9716"
                  style={styles.input}
                  step="any"
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label>Longitude (optional)</label>
                <input
                  type="text"
                  name="lng"
                  value={localLocation.lng === null ? "" : localLocation.lng}
                  onChange={handleLocalChange}
                  placeholder="e.g., 77.5946"
                  style={styles.input}
                  step="any"
                />
              </div>
            </div>
            
            <div style={styles.manualFooter}>
              <button 
                onClick={handleSave}
                style={styles.saveButton}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "💾 Save Address"}
              </button>
              <button 
                onClick={handleBackToMap}
                style={styles.backToMapButton}
              >
                🗺️ Back to Map
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render location preview
  const renderLocationPreview = () => {
    if (!selectedLocation.address) return null;
    
    const displayLat = formatCoordinate(selectedLocation.lat);
    const displayLng = formatCoordinate(selectedLocation.lng);
    
    return (
      <div style={styles.locationPreview}>
        <div style={styles.locationHeader}>
          <h4>📍 Current Location</h4>
          <div style={styles.locationActionButtons}>
            <button 
              type="button"
              onClick={() => setManualEditMode(true)}
              style={styles.editLocationBtn}
              title="Edit location details"
            >
              ✏️ Edit
            </button>
            <button 
              type="button"
              onClick={() => setShowMap(true)}
              style={styles.mapButtonSmall}
              title="Edit on map"
            >
              🗺️ Map
            </button>
          </div>
        </div>
        
        <div style={styles.locationDetails}>
          <p><strong>Address:</strong> {selectedLocation.address}</p>
          {selectedLocation.area && <p><strong>Area:</strong> {selectedLocation.area}</p>}
          {selectedLocation.city && <p><strong>City:</strong> {selectedLocation.city}</p>}
          {selectedLocation.state && <p><strong>State:</strong> {selectedLocation.state}</p>}
          {selectedLocation.pincode && <p><strong>Pincode:</strong> {selectedLocation.pincode}</p>}
          {displayLat && displayLng && (
            <p><strong>Coordinates:</strong> {displayLat}, {displayLng}</p>
          )}
        </div>
      </div>
    );
  };

  // Render BHK Configuration
  const renderBhkConfig = () => {
    if (!isToLet) return null;
    
    const bhkNumber = parseInt(form.bhk_type) || 1;
    
    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🏠 BHK Configuration</h3>
        <p style={styles.note}>Configure rooms and bathrooms for each BHK type</p>
        <div style={styles.grid}>
          {bhkNumber >= 1 && (
            <>
              <div style={styles.inputGroup}>
                <label>1 BHK - Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms_1bhk"
                  placeholder="e.g., 1"
                  value={bhkConfig.bedrooms_1bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label>1 BHK - Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms_1bhk"
                  placeholder="e.g., 1"
                  value={bhkConfig.bathrooms_1bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
            </>
          )}
          {bhkNumber >= 2 && (
            <>
              <div style={styles.inputGroup}>
                <label>2 BHK - Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms_2bhk"
                  placeholder="e.g., 2"
                  value={bhkConfig.bedrooms_2bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label>2 BHK - Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms_2bhk"
                  placeholder="e.g., 2"
                  value={bhkConfig.bathrooms_2bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
            </>
          )}
          {bhkNumber >= 3 && (
            <>
              <div style={styles.inputGroup}>
                <label>3 BHK - Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms_3bhk"
                  placeholder="e.g., 3"
                  value={bhkConfig.bedrooms_3bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label>3 BHK - Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms_3bhk"
                  placeholder="e.g., 3"
                  value={bhkConfig.bathrooms_3bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
            </>
          )}
          {bhkNumber >= 4 && (
            <>
              <div style={styles.inputGroup}>
                <label>4 BHK - Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms_4bhk"
                  placeholder="e.g., 4"
                  value={bhkConfig.bedrooms_4bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label>4 BHK - Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms_4bhk"
                  placeholder="e.g., 4"
                  value={bhkConfig.bathrooms_4bhk}
                  onChange={handleBhkConfigChange}
                  style={styles.input}
                  min="1"
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render rates
  const renderRates = () => {
    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          {isToLet ? "💰 Rental Amount (₹/Month)" : 
           isCoLiving ? "💰 Co-Living Rates (₹/Month)" : 
           "💰 Room Rates (₹/Month)"}
        </h3>
        
        {isToLet ? (
          <div style={styles.grid}>
            <div style={styles.inputGroup}>
              <label>1 BHK Rent *</label>
              <input
                type="number"
                name="price_1bhk"
                placeholder="₹"
                value={roomRates.price_1bhk}
                onChange={handleRateChange}
                style={styles.input}
                min="0"
                required
              />
            </div>
            {parseInt(form.bhk_type) >= 2 && (
              <div style={styles.inputGroup}>
                <label>2 BHK Rent</label>
                <input
                  type="number"
                  name="price_2bhk"
                  placeholder="₹"
                  value={roomRates.price_2bhk}
                  onChange={handleRateChange}
                  style={styles.input}
                  min="0"
                />
              </div>
            )}
            {parseInt(form.bhk_type) >= 3 && (
              <div style={styles.inputGroup}>
                <label>3 BHK Rent</label>
                <input
                  type="number"
                  name="price_3bhk"
                  placeholder="₹"
                  value={roomRates.price_3bhk}
                  onChange={handleRateChange}
                  style={styles.input}
                  min="0"
                />
              </div>
            )}
            {parseInt(form.bhk_type) >= 4 && (
              <div style={styles.inputGroup}>
                <label>4 BHK Rent</label>
                <input
                  type="number"
                  name="price_4bhk"
                  placeholder="₹"
                  value={roomRates.price_4bhk}
                  onChange={handleRateChange}
                  style={styles.input}
                  min="0"
                />
              </div>
            )}
          </div>
        ) : isCoLiving ? (
          <div style={styles.ratesGrid}>
            <div style={styles.inputGroup}>
              <label>Co-Living Single Room *</label>
              <input
                type="number"
                name="co_living_single_room"
                placeholder="₹"
                value={roomRates.co_living_single_room}
                onChange={handleRateChange}
                style={styles.input}
                min="0"
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label>Co-Living Double Room</label>
              <input
                type="number"
                name="co_living_double_room"
                placeholder="₹"
                value={roomRates.co_living_double_room}
                onChange={handleRateChange}
                style={styles.input}
                min="0"
              />
            </div>
            <div style={styles.inputGroup}>
              <label>Co-Living 3 Sharing (NEW)</label>
              <input
                type="number"
                name="coliving_three_sharing"
                placeholder="₹"
                value={roomRates.coliving_three_sharing}
                onChange={handleRateChange}
                style={styles.input}
                min="0"
              />
            </div>
            <div style={styles.inputGroup}>
              <label>Co-Living 4 Sharing (NEW)</label>
              <input
                type="number"
                name="coliving_four_sharing"
                placeholder="₹"
                value={roomRates.coliving_four_sharing}
                onChange={handleRateChange}
                style={styles.input}
                min="0"
              />
            </div>
          </div>
        ) : (
          <>
            <p style={styles.note}>Enter amount for each room type (leave blank if not available)</p>
            <div style={styles.ratesGrid}>
              {[
                { key: "single_sharing", label: "Single Sharing" },
                { key: "double_sharing", label: "Double Sharing" },
                { key: "triple_sharing", label: "Triple Sharing" },
                { key: "four_sharing", label: "Four Sharing" },
                { key: "single_room", label: "Single Room" },
                { key: "double_room", label: "Double Room" },
              ].map((room) => (
                <div key={room.key} style={styles.rateInputGroup}>
                  <label>{room.label}</label>
                  <input
                    type="number"
                    name={room.key}
                    placeholder="₹"
                    value={roomRates[room.key]}
                    onChange={handleRateChange}
                    style={styles.input}
                    min="0"
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  /* ================= PROTECTION ================= */
  if (authLoading || pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (role !== "owner") return <Navigate to="/" replace />;

  const isSubmitDisabled = updating || 
    !selectedLocation.address || 
    !form.pg_name?.trim() ||
    !form.contact_person?.trim() ||
    !form.contact_phone?.trim();

  // Rest of the render method with all form sections (same as before, but add Minimum Stay section)
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>✏️ Edit Property</h2>
        
        {/* Basic Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Basic Information</h3>
          {/* Basic Info form fields - same as in your original EditPG */}
          {/* ... (keep your existing form fields) ... */}
        </div>

        {/* Location */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Location *</h3>
          <div style={styles.locationButtons}>
            <button 
              type="button" 
              onClick={() => setShowMap(true)}
                            style={styles.mapButton}
            >
              🗺️ Edit on OpenStreetMap
            </button>
            
            <button 
              type="button" 
              onClick={() => setManualEditMode(true)}
              style={styles.manualAddressButton}
            >
              📝 Edit Address Manually
            </button>
          </div>
          
          {renderLocationPreview()}
          
          {!selectedLocation.address && (
            <div style={styles.locationWarning}>
              ⚠️ Please select a location using the map or enter manually
            </div>
          )}
        </div>

        {/* BHK Configuration */}
        {renderBhkConfig()}

        {/* Room Rates */}
        {renderRates()}

        {/* Facilities & Amenities */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🏠 Facilities & Amenities (Select Available)</h3>
          <div style={styles.checkboxGrid}>
            {/* Food */}
            {!isToLet && (
              <>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" name="food_available" checked={form.food_available} onChange={handleChange} style={styles.checkbox} />
                  🍽️ Food Available
                </label>
                {form.food_available && (
                  <>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={{ marginRight: "10px" }}>Food Type:</label>
                      <select name="food_type" value={form.food_type} onChange={handleChange} style={{...styles.input, width: "150px", display: "inline-block"}}>
                        <option value="veg">Vegetarian</option>
                        <option value="non_veg">Non-Vegetarian</option>
                        <option value="both">Both</option>
                      </select>
                      <label style={{ marginLeft: "20px", marginRight: "10px" }}>Meals per Day:</label>
                      <input
                        type="number"
                        name="meals_per_day"
                        placeholder="e.g., 3"
                        value={form.meals_per_day}
                        onChange={handleChange}
                        style={{...styles.input, width: "100px", display: "inline-block"}}
                        min="1"
                        max="4"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            
            {/* Room Furnishings */}
            <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
              <h4>🛏️ Room Furnishings:</h4>
            </div>
            {[
              { key: "cupboard_available", label: "👔 Cupboard/Wardrobe" },
              { key: "table_chair_available", label: "💺 Study Table & Chair" },
              { key: "dining_table_available", label: "🍽️ Dining Table" },
              { key: "attached_bathroom", label: "🚽 Attached Bathroom" },
              { key: "balcony_available", label: "🌿 Balcony" },
              { key: "wall_mounted_clothes_hook", label: "🪝 Wall-mounted Clothes Hook" },
              { key: "bed_with_mattress", label: "🛏️ Bed with Mattress" },
              { key: "fan_light", label: "💡 Fan & Light" },
              { key: "kitchen_room", label: "🍳 Kitchen Room" },
            ].map((item) => (
              <label key={item.key} style={styles.checkboxLabel}>
                <input type="checkbox" name={item.key} checked={form[item.key]} onChange={handleChange} style={styles.checkbox} />
                {item.label}
              </label>
            ))}
            
            {/* Building Facilities */}
            <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
              <h4>🏢 Building Facilities:</h4>
            </div>
            {[
              { key: "ac_available", label: "❄️ Air Conditioner" },
              { key: "wifi_available", label: "📶 Wi-Fi / Internet" },
              { key: "tv", label: "📺 Television" },
              { key: "parking_available", label: "🚗 Car Parking" },
              { key: "bike_parking", label: "🏍️ Bike Parking" },
              { key: "laundry_available", label: "🧺 Laundry Service" },
              { key: "washing_machine", label: "🧼 Washing Machine" },
              { key: "refrigerator", label: "🧊 Refrigerator" },
              { key: "microwave", label: "🍳 Microwave" },
              { key: "geyser", label: "🚿 Geyser" },
              { key: "power_backup", label: "🔋 Power Backup" },
              { key: "lift_elevator", label: "⬆️ Lift / Elevator" },
              { key: "cctv", label: "📹 CCTV Surveillance" },
              { key: "security_guard", label: "🛡️ Security Guard" },
              { key: "gym", label: "🏋️ Gym / Fitness" },
              { key: "housekeeping", label: "🧹 Housekeeping" },
              { key: "water_purifier", label: "💧 Water Purifier (RO)" },
              { key: "fire_safety", label: "🔥 Fire Safety System" },
              { key: "study_room", label: "📚 Study Room" },
              { key: "common_tv_lounge", label: "📺 Common TV / Lounge" },
              { key: "balcony_open_space", label: "🌿 Balcony / Open Space" },
              { key: "water_24x7", label: "💦 24×7 Water Supply" },
            ].map((facility) => (
              <label key={facility.key} style={styles.checkboxLabel}>
                <input type="checkbox" name={facility.key} checked={form[facility.key]} onChange={handleChange} style={styles.checkbox} />
                {facility.label}
              </label>
            ))}
            
            {/* Water Type */}
            <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
              <label style={{ marginRight: "10px" }}>💧 Water Source:</label>
              <select name="water_type" value={form.water_type} onChange={handleChange} style={{...styles.input, width: "150px", display: "inline-block"}}>
                <option value="borewell">Borewell</option>
                <option value="kaveri">Kaveri</option>
                <option value="both">Both</option>
                <option value="municipal">Municipal</option>
              </select>
            </div>
            
            {/* Co-Living Inclusions */}
            {isCoLiving && (
              <>
                <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
                  <h4>🤝 Co-Living Inclusions:</h4>
                </div>
                {[
                  { key: "co_living_fully_furnished", label: "🛋️ Fully Furnished" },
                  { key: "co_living_food_included", label: "🍽️ Food Included" },
                  { key: "co_living_wifi_included", label: "📶 Wi-Fi Included" },
                  { key: "co_living_housekeeping", label: "🧹 Housekeeping" },
                  { key: "co_living_power_backup", label: "🔋 Power Backup" },
                  { key: "co_living_maintenance", label: "🔧 Maintenance" },
                ].map((item) => (
                  <label key={item.key} style={{...styles.checkboxLabel, color: "#2E7D32"}}>
                    <input type="checkbox" name={item.key} checked={form[item.key]} onChange={handleChange} style={styles.checkbox} />
                    {item.label}
                  </label>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Rules & Restrictions */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📜 Rules & Restrictions</h3>
          <div style={styles.checkboxGrid}>
            {/* Gender Selection */}
            <div style={{ gridColumn: "1 / -1", marginBottom: "10px" }}>
              <h4>👥 Allowed For:</h4>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <label style={styles.checkboxLabel}>
                  <input 
                    type="checkbox" 
                    name="boys_only" 
                    checked={form.boys_only} 
                    onChange={handleChange} 
                    style={styles.checkbox} 
                  />
                  👨 Boys Only
                </label>
                <label style={styles.checkboxLabel}>
                  <input 
                    type="checkbox" 
                    name="girls_only" 
                    checked={form.girls_only} 
                    onChange={handleChange} 
                    style={styles.checkbox} 
                  />
                  👩 Girls Only
                </label>
                <label style={styles.checkboxLabel}>
                  <input 
                    type="checkbox" 
                    name="co_living_allowed" 
                    checked={form.co_living_allowed} 
                    onChange={handleChange} 
                    style={styles.checkbox} 
                  />
                  🤝 Co-Living (Mixed)
                </label>
              </div>
            </div>
            
            {/* Rules List */}
            {[
              { key: "visitor_allowed", label: "👥 Visitors Allowed" },
              { key: "visitor_time_restricted", label: "⏰ Visitor Time Restricted" },
              { key: "couple_allowed", label: "❤️ Couples Allowed" },
              { key: "family_allowed", label: "👨‍👩‍👧‍👦 Family Allowed" },
              { key: "smoking_allowed", label: "🚬 Smoking Allowed" },
              { key: "drinking_allowed", label: "🍺 Drinking Allowed" },
              { key: "pets_allowed", label: "🐕 Pets Allowed" },
              { key: "late_night_entry_allowed", label: "🌙 Late Night Entry" },
              { key: "outside_food_allowed", label: "🍕 Outside Food Allowed" },
              { key: "parties_allowed", label: "🎉 Parties Allowed" },
              { key: "loud_music_restricted", label: "🔇 Loud Music Restricted" },
              { key: "lock_in_period", label: "🔒 Lock-in Period" },
              { key: "agreement_mandatory", label: "📝 Agreement Mandatory" },
              { key: "id_proof_mandatory", label: "🆔 ID Proof Mandatory" },
              { key: "office_going_only", label: "💼 Office-Going Only" },
              { key: "students_only", label: "🎓 Students Only" },
              { key: "subletting_allowed", label: "🔄 Sub-letting Allowed" },
            ].map((rule) => (
              <label key={rule.key} style={styles.checkboxLabel}>
                <input type="checkbox" name={rule.key} checked={form[rule.key]} onChange={handleChange} style={styles.checkbox} />
                {rule.label}
              </label>
            ))}
            
            {/* Minimum Stay Section */}
            <div style={{ gridColumn: "1 / -1", marginTop: 15, padding: "15px", background: "#f0f8ff", borderRadius: "8px", border: "1px solid #b0d4ff" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#1976d2" }}>⏱️ Minimum Stay Options</h4>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" name="min_stay_available" checked={form.min_stay_available} onChange={handleChange} style={styles.checkbox} />
                  Enable Minimum Stay Requirement
                </label>
                {form.min_stay_available && (
                  <div style={{ ...styles.inputGroup, width: "200px" }}>
                    <label>Minimum Stay Days</label>
                    <input 
                      type="number" 
                      name="min_stay_days" 
                      placeholder="e.g., 30, 60, 90" 
                      value={form.min_stay_days} 
                      onChange={handleChange} 
                      style={styles.input} 
                      min="1"
                    />
                    <small style={{ fontSize: "11px", color: "#666" }}>Minimum number of days tenant must stay</small>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Time-specific fields */}
          <div style={{...styles.grid, marginTop: "15px"}}>
            {form.visitor_time_restricted && (
              <div style={styles.inputGroup}>
                <label>Visitors Allowed Till</label>
                <input type="time" name="visitors_allowed_till" value={form.visitors_allowed_till} onChange={handleChange} style={styles.input} />
              </div>
            )}
            {!form.late_night_entry_allowed && (
              <div style={styles.inputGroup}>
                <label>Entry Curfew Time</label>
                <input type="time" name="entry_curfew_time" value={form.entry_curfew_time} onChange={handleChange} style={styles.input} />
              </div>
            )}
            {form.lock_in_period && (
              <div style={styles.inputGroup}>
                <label>Minimum Stay (Months)</label>
                <input type="number" name="min_stay_months" value={form.min_stay_months} onChange={handleChange} style={styles.input} min="1" placeholder="e.g., 6" />
              </div>
            )}
          </div>
        </div>

        {/* Additional Details & Charges */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>💰 Additional Details & Charges</h3>
          <div style={styles.grid}>
            <div style={styles.inputGroup}>
              <label>Security Deposit (₹)</label>
              <input 
                type="number" 
                name="security_deposit" 
                placeholder="e.g., 10000" 
                value={form.security_deposit} 
                onChange={handleChange} 
                style={styles.input} 
                min="0" 
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label>Maintenance Charges (₹/Month)</label>
              <input 
                type="number" 
                name="maintenance_amount" 
                placeholder="e.g., 1000" 
                value={form.maintenance_amount} 
                onChange={handleChange} 
                style={styles.input} 
                min="0" 
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label>Notice Period</label>
              <select name="notice_period" value={form.notice_period} onChange={handleChange} style={styles.input}>
                <option value="1">1 Month</option>
                <option value="2">2 Months</option>
                <option value="3">3 Months</option>
              </select>
            </div>
            
            <div style={styles.inputGroup}>
              <label>Total {isToLet ? "Properties" : "Rooms"}</label>
              <input 
                type="number" 
                name="total_rooms" 
                placeholder={isToLet ? "e.g., 1" : "e.g., 10"} 
                value={form.total_rooms} 
                onChange={handleChange} 
                style={styles.input} 
                min="1" 
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label>Available {isToLet ? "Properties" : "Rooms"}</label>
              <input 
                type="number" 
                name="available_rooms" 
                placeholder={isToLet ? "e.g., 1" : "e.g., 5"} 
                value={form.available_rooms} 
                onChange={handleChange} 
                style={styles.input} 
                min="0" 
              />
            </div>
          </div>
          
          <div style={styles.inputGroup}>
            <label>Description *</label>
            <textarea
              name="description"
              placeholder={
                isToLet 
                  ? "Describe your house/flat, bedrooms, bathrooms, nearby facilities..." 
                  : "Describe your property, amenities, nearby facilities..."
              }
              value={form.description}
              onChange={handleChange}
              style={styles.textarea}
              rows="4"
              required
            />
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📞 Contact Information</h3>
          <div style={styles.grid}>
            <div style={styles.inputGroup}>
              <label>Contact Person *</label>
              <input name="contact_person" placeholder="e.g., Owner/Manager Name" value={form.contact_person} onChange={handleChange} style={styles.input} required />
            </div>
            
            <div style={styles.inputGroup}>
              <label>Contact Email</label>
              <input type="email" name="contact_email" placeholder="contact@example.com" value={form.contact_email} onChange={handleChange} style={styles.input} />
            </div>
            
            <div style={styles.inputGroup}>
              <label>Contact Phone *</label>
              <input type="tel" name="contact_phone" placeholder="9876543210" value={form.contact_phone} onChange={handleChange} style={styles.input} required pattern="[0-9]{10}" />
            </div>
          </div>
        </div>

        {/* Photo Upload */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📷 Property Photos</h3>
          <p style={styles.note}>Add new photos or remove existing ones. Maximum 10 photos allowed.</p>
          
          {/* Existing Photos */}
          {existingPhotos.length > 0 && (
            <div style={styles.existingPhotos}>
              <h4>Existing Photos:</h4>
              <div style={styles.photoPreview}>
                {existingPhotos.map((photoUrl, index) => (
                  <div key={index} style={styles.photoItem}>
                    <img 
                      src={getImageUrl(photoUrl)} 
                      alt={`Property ${index + 1}`} 
                      style={styles.photoThumbnail}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/100x75?text=Photo";
                      }}
                    />
                    <button 
                      type="button" 
                      onClick={() => removeExistingPhoto(index, photoUrl)} 
                      style={styles.removePhotoBtn}
                      title="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* New Photo Upload */}
          <div style={styles.fileUpload}>
            <label htmlFor="photo-upload" style={styles.fileUploadLabel}>
              📁 Add New Photos
            </label>
            <input id="photo-upload" type="file" accept="image/*" multiple onChange={handlePhotoUpload} style={styles.fileInput} />
          </div>
          
          {/* New Photos Preview */}
          {photos.length > 0 && (
            <div style={styles.newPhotos}>
              <h4>New Photos to Upload:</h4>
              <div style={styles.photoPreview}>
                {photos.map((photo, index) => (
                  <div key={index} style={styles.photoItem}>
                    <span style={styles.photoName}>
                      {photo.name.length > 20 ? photo.name.substring(0, 20) + "..." : photo.name}
                    </span>
                    <button type="button" onClick={() => removePhoto(index)} style={styles.removePhotoBtn} title="Remove photo">✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <p style={styles.photoCount}>
            Total Photos: {existingPhotos.length - photosToDelete.length + photos.length}/10
          </p>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <button 
            onClick={() => navigate("/owner/dashboard")} 
            style={styles.cancelBtn}
          >
            ↩️ Back to Dashboard
          </button>
          
          <button 
            onClick={handleSubmit} 
            disabled={isSubmitDisabled}
            style={{
              ...styles.submitBtn,
              ...(isSubmitDisabled ? styles.submitBtnDisabled : {})
            }}
          >
            {updating ? "Updating Property..." : "💾 Update Property"}
          </button>
        </div>
      </div>

      {/* OpenStreetMap Modal */}
      {showMap && <OpenStreetMapModal />}
      
      {/* Manual Address Entry Modal */}
      {manualEditMode && <ManualAddressForm />}
    </div>
  );
}

// Styles
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  card: {
    background: "#ffffff",
    width: "100%",
    maxWidth: "1200px",
    padding: "30px",
    borderRadius: "20px",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.2)",
    margin: "20px 0",
  },
  title: {
    textAlign: "center",
    marginBottom: "30px",
    color: "#333",
    fontSize: "28px",
    fontWeight: "600",
  },
  section: {
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "1px solid #eee",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#444",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  note: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "15px",
    fontStyle: "italic",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "15px",
    marginBottom: "15px",
  },
  ratesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  rateInputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  input: {
    padding: "12px 14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border 0.2s, box-shadow 0.2s",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "12px 14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border 0.2s, box-shadow 0.2s",
    width: "100%",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  locationButtons: {
    display: "flex",
    gap: "15px",
    marginBottom: "15px",
    flexWrap: "wrap",
  },
  mapButton: {
    padding: "12px 20px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s",
    flex: "1",
    minWidth: "200px",
  },
  mapButtonSmall: {
    padding: "6px 12px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  manualAddressButton: {
    padding: "12px 20px",
    background: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s",
    flex: "1",
    minWidth: "200px",
  },
  locationPreview: {
    background: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
    marginTop: "10px",
    borderLeft: "4px solid #4CAF50",
  },
  locationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  locationActionButtons: {
    display: "flex",
    gap: "10px",
  },
  editLocationBtn: {
    background: "#2196F3",
    color: "white",
    border: "none",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  locationDetails: {
    fontSize: "14px",
    color: "#555",
  },
  locationWarning: {
    background: "#FFF3CD",
    border: "1px solid #FFEEBA",
    color: "#856404",
    padding: "10px",
    borderRadius: "6px",
    marginTop: "10px",
    fontSize: "14px",
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "12px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#555",
    padding: "8px",
    borderRadius: "6px",
    transition: "background 0.2s",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
  },
  existingPhotos: {
    marginBottom: "20px",
  },
  newPhotos: {
    marginBottom: "20px",
  },
  fileUpload: {
    marginBottom: "15px",
  },
  fileUploadLabel: {
    display: "inline-block",
    padding: "12px 20px",
    background: "#667eea",
    color: "white",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s",
  },
  fileInput: {
    display: "none",
  },
  photoPreview: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  photoItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "5px",
    background: "#f8f9fa",
    padding: "10px",
    borderRadius: "6px",
    fontSize: "14px",
    border: "1px solid #e9ecef",
    position: "relative",
  },
  photoThumbnail: {
    width: "100px",
    height: "75px",
    objectFit: "cover",
    borderRadius: "4px",
  },
  photoName: {
    maxWidth: "150px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  removePhotoBtn: {
    background: "#ff4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: "-8px",
    right: "-8px",
  },
  photoCount: {
    fontSize: "14px",
    color: "#666",
    marginTop: "10px",
  },
  actionButtons: {
    display: "flex",
    gap: "15px",
    marginTop: "20px",
  },
  cancelBtn: {
    flex: "1",
    padding: "16px",
    background: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  submitBtn: {
    flex: "2",
    padding: "16px",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  submitBtnDisabled: {
    opacity: "0.6",
    cursor: "not-allowed",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "white",
  },
  spinner: {
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #3498db",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    animation: "spin 1s linear infinite",
  },
  // Map modal styles
  mapModalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  mapModal: {
    background: "white",
    borderRadius: "12px",
    width: "95%",
    maxWidth: "1000px",
    maxHeight: "95vh",
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  },
  mapHeader: {
    padding: "15px 20px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#f8f9fa",
    flexWrap: "wrap",
    gap: "10px",
  },
  mapControls: {
    display: "flex",
    flex: "1",
    flexDirection: "column",
    gap: "10px",
    minWidth: "300px",
  },
  currentLocationButton: {
    padding: "10px 15px",
    background: "#FF9800",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    width: "100%",
    justifyContent: "center",
  },
  mapSearch: {
    display: "flex",
    gap: "10px",
    width: "100%",
  },
  searchInput: {
    flex: "1",
    padding: "10px 15px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    minWidth: "200px",
  },
  searchButton: {
    padding: "10px 15px",
    background: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    minWidth: "100px",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    flexShrink: 0,
  },
  mapContainer: {
    position: "relative",
  },
  leafletMap: {
    height: "400px",
    width: "100%",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.8)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  mapFooter: {
    padding: "15px 20px",
    display: "flex",
    gap: "15px",
    justifyContent: "center",
    background: "#f8f9fa",
    borderTop: "1px solid #eee",
  },
  confirmButton: {
    padding: "12px 24px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  manualButton: {
    padding: "12px 24px",
    background: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  manualModalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1001,
  },
  manualModal: {
    background: "white",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "800px",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  },
  manualHeader: {
    padding: "20px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#f8f9fa",
  },
  manualForm: {
    padding: "20px",
  },
  manualFooter: {
    padding: "20px",
    display: "flex",
    gap: "15px",
    justifyContent: "center",
    background: "#f8f9fa",
    borderTop: "1px solid #eee",
  },
  saveButton: {
    padding: "12px 24px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  backToMapButton: {
    padding: "12px 24px",
    background: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

export default EditPG;